import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NavigationProvider } from './context/NavigationContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import ProjectDashboardPage from './pages/ProjectDashboardPage';
import NCRPage from './pages/NCRPage';
import DPRPage from './pages/DPRPage';
import MOMPage from './pages/MOMPage';
import SiteInstructionsPage from './pages/SiteInstructionsPage';
import RFIPage from './pages/RFIPage';
import MIRPage from './pages/MIRPage';
import WIRPage from './pages/WIRPage';
import SnagRegisterPage from './pages/SnagRegisterPage';
import QualityAuditPage from './pages/QualityAuditPage';
import NotificationsPage from './pages/NotificationsPage';
import SearchPage from './pages/SearchPage';
import DocumentsPage from './pages/DocumentsPage';
import AuditTrailPage from './pages/AuditTrailPage';
import UsersPage from './pages/UsersPage';
import ClientDashboardPage from './pages/ClientDashboardPage';
import AdminPage from './pages/AdminPage';
import ExcelImportPage from './pages/ExcelImportPage';
import MainLayout from './components/MainLayout';

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <>{children}</>;
};

const MainApp: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [currentPage, setCurrentPage] = useState('dashboard');

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <DashboardPage />;
      case 'project-dashboard':
        return <ProjectDashboardPage />;
      case 'ncr':
        return <NCRPage />;
      case 'dpr':
        return <DPRPage />;
      case 'mom':
        return <MOMPage />;
      case 'site-instructions':
        return <SiteInstructionsPage />;
      case 'rfi':
        return <RFIPage />;
      case 'mir':
        return <MIRPage />;
      case 'wir':
        return <WIRPage />;
      case 'snag-register':
        return <SnagRegisterPage />;
      case 'quality-audit':
        return <QualityAuditPage />;
      case 'notifications':
        return <NotificationsPage />;
      case 'search':
        return <SearchPage />;
      case 'documents':
        return <DocumentsPage />;
      case 'audit-trail':
        return <AuditTrailPage />;
      case 'settings':
        return <UsersPage />;
      case 'client-dashboard':
        return <ClientDashboardPage />;
      case 'admin':
        return <AdminPage />;
      case 'excel-import':
        return <ExcelImportPage />;
      default:
        return (
          <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>
            <h3>{currentPage.charAt(0).toUpperCase() + currentPage.slice(1)} module coming soon...</h3>
          </div>
        );
    }
  };

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/login" element={<Navigate to="/" replace />} />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <NavigationProvider onNavigate={setCurrentPage}>
              <MainLayout currentPage={currentPage} onNavigate={setCurrentPage}>
                {renderPage()}
              </MainLayout>
            </NavigationProvider>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#2E75B6',
          borderRadius: 8,
          fontFamily: 'Arial, sans-serif',
        },
      }}
    >
      <AuthProvider>
        <Router>
          <MainApp />
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;